/**
 * Supabase RPC Service
 * Servicio centralizado para llamar funciones RPC de Supabase desde el frontend
 * Proporciona tipado TypeScript, validación y manejo centralizado de errores
 */

import { supabaseAuth, DB_SCHEMA } from './supabase-auth';
import { logger } from '../utils/logger';
import { SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// TIPOS PARA RESPUESTAS RPC
// ============================================================================

/**
 * Extensión de SupabaseClient para tipado correcto del método RPC
 */
declare module '@supabase/supabase-js' {
  interface SupabaseClient {
    rpc<T = any>(
      fn: string,
      args?: any,
      options?: any
    ): Promise<{ data: T | null; error: any | null }>;
  }
}

/**
 * Mediciones agregadas por intervalo de tiempo
 */
export interface MedicionAgregada {
  fecha_agregada: string;
  metricaid: number;
  valor_promedio: number;
  valor_min: number;
  valor_max: number;
  cantidad: number;
}

/**
 * Estadísticas de mediciones para KPIs
 */
export interface EstadisticaMedicion {
  metricaid: number;
  nombre_metrica: string;
  unidad: string;
  promedio: number;
  minimo: number;
  maximo: number;
  desviacion: number;
  cantidad_total: number;
  ultima_fecha: string;
  ultima_valor: number;
}

/**
 * Alertas filtradas por nodo
 */
export interface AlertaPorNodo {
  alertaid: string;
  fecha: string;
  valor: number;
  regla_nombre: string;
  criticidad: string;
  metricaid: number;
  metrica_nombre: string;
  localizacionid: number;
}

/**
 * Umbrales con información de métrica y criticidad
 */
export interface UmbralPorNodo {
  umbralid: number;
  minimo: number;
  maximo: number;
  estandar?: number;
  metricaid: number;
  metrica_nombre: string;
  unidad: string;
  criticidad: string;
}

/**
 * Umbrales con estadísticas históricas
 */
export interface UmbralConEstadisticas {
  umbralid: number;
  nodoid: number;
  metricaid: number;
  minimo: number;
  maximo: number;
  criticidad: string;
  total_alertas: number;
  tiempo_promedio_alerta: number;
}

/**
 * Nodos con alertas activas
 */
export interface NodoConAlertas {
  nodoid: number | string;
  nodo: string;
  total_alertas: number;
  criticidad_maxima: string;
}

/**
 * Resumen ligero de nodos para visualización del mapa
 */
export interface ResumenMapaNodo {
  nodoid: number;
  nodo: string;
  ubicacionid: number;
  latitud: number;
  longitud: number;
  ultima_medicion_fecha: string;
  total_mediciones_7d: number;
  alertas_activas: number;
  metricas_disponibles: number[];
}

/**
 * Medición con información de agregación
 */
export interface MedicionConAgregacion {
  medicionid: number;
  localizacionid: number;
  fecha: string;
  medicion: number;
  es_agregada: boolean;
}

// ============================================================================
// SERVICIO RPC
// ============================================================================

export class SupabaseRPCService {
  private static readonly DEBUG = process.env.NODE_ENV === 'development';

  /**
   * Calcula el intervalo de agregación en minutos basado en el rango de fechas
   * @param startDate Fecha de inicio
   * @param endDate Fecha de fin
   * @returns Intervalo en minutos (60, 360, 1440 o 4320)
   */
  static calculateInterval(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = (end.getTime() - start.getTime()) / (1000 * 3600 * 24);

    if (daysDiff <= 7) return 60; // 1 hora para <7 días
    if (daysDiff <= 30) return 360; // 6 horas para 7-30 días
    if (daysDiff <= 90) return 1440; // 1 día para 30-90 días
    return 4320; // 3 días para >90 días
  }

  /**
   * Obtiene mediciones agregadas por intervalo de tiempo
   * Reduce datos transferidos hasta 95%
   * @param params Parámetros de la consulta
   * @returns Array de mediciones agregadas
   */
  static async getMedicionesAgregadas(params: {
    nodoid: number;
    metricaid?: number;
    startDate?: string;
    endDate?: string;
    intervalMinutes?: number;
  }): Promise<MedicionAgregada[]> {
    try {
      // Validación de parámetros
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      // Calcular intervalo si no se proporciona
      let interval = params.intervalMinutes;
      if (!interval && params.startDate && params.endDate) {
        interval = this.calculateInterval(params.startDate, params.endDate);
      }

      if (this.DEBUG) {
        logger.debug('SupabaseRPCService', 'getMedicionesAgregadas', {
          nodoid: params.nodoid,
          intervalMinutes: interval
        });
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_mediciones_agregadas', {
          p_nodoid: params.nodoid,
          p_metricaid: params.metricaid || null,
          p_start_date: params.startDate ? `${params.startDate} 00:00:00` : null,
          p_end_date: params.endDate ? `${params.endDate} 23:59:59` : null,
          p_interval_minutes: interval || 360
        });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      logger.error('SupabaseRPCService', 'Error in getMedicionesAgregadas', { error: err?.message });
      throw err;
    }
  }

  /**
   * Obtiene estadísticas de mediciones para KPIs
   * Calcula estadísticas en base de datos, no en frontend
   * @param params Parámetros de la consulta
   * @returns Array de estadísticas por métrica
   */
  /**
   * Obtiene alertas filtradas por nodo
   * Evita cargar todas las alertas y luego filtrar en frontend
   * @param params Parámetros de la consulta
   * @returns Array de alertas para el nodo
   */
  static async getAlertasPorNodo(params: {
    nodoid: number;
    startDate?: string;
    endDate?: string;
  }): Promise<AlertaPorNodo[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }


      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_alertas_por_nodo', {
          p_nodoid: params.nodoid,
          p_start_date: params.startDate ? `${params.startDate} 00:00:00` : null,
          p_end_date: params.endDate ? `${params.endDate} 23:59:59` : null
        });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getAlertasPorNodo:', err);
      throw err;
    }
  }

  /**
   * Obtiene umbrales con información de métrica y criticidad por nodo
   * @param params Parámetros de la consulta
   * @returns Array de umbrales para el nodo
   */
  static async getUmbralesPorNodo(params: {
    nodoid: number;
  }): Promise<UmbralPorNodo[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }


      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_umbrales_por_nodo', {
          p_nodoid: params.nodoid
        });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getUmbralesPorNodo:', err);
      throw err;
    }
  }

  /**
   * Obtiene umbrales con estadísticas históricas (para UMBRALES POR LOCALIZACIÓN)
   * Consolida datos de alertas y umbrales
   * @param params Parámetros de la consulta
   * @returns Array de umbrales con estadísticas
   */
  /**
   * Obtiene nodos con alertas activas
   * Evita cargar todos los nodos y filtrar después
   * @returns Array de nodos que tienen alertas
   */
  static async getNodosConAlertas(): Promise<NodoConAlertas[]> {
    try {

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_nodos_con_alertas', {});

      if (error) {
        // Si la función aún no existe, retornar array vacío
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_nodos_con_alertas no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getNodosConAlertas:', err);
      throw err;
    }
  }

  /**
   * Obtiene resumen ligero de nodos para visualización del mapa
   * Reduce datos transferidos al mínimo para carga inicial rápida
   * Incluye última medición, conteo reciente, alertas y métricas disponibles
   * @param params Parámetros opcionales de filtro
   * @returns Array de resumen de nodos para el mapa
   */
  static async getResumenMapaNodos(params?: {
    ubicacionId?: number;
  }): Promise<ResumenMapaNodo[]> {
    try {

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_resumen_mapa_nodos', {
          p_ubicacionid: params?.ubicacionId || null
        });

      if (error) {
        // Si la función aún no existe, retornar array vacío
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_resumen_mapa_nodos no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getResumenMapaNodos:', err);
      throw err;
    }
  }

  /**
   * Obtiene mediciones agregadas por rango (optimizado para 90 días)
   * La agregación se hace automáticamente en la BD según el rango:
   * - ≤7 días: Datos detallados
   * - 7-30 días: Agregados por hora
   * - >30 días: Agregados por 6 horas
   * @param params Parámetros de la consulta
   * @returns Array de mediciones (detalladas o agregadas)
   */
  static async getMedicionesAgregadasPorRango(params: {
    localizacionids: number[];
    startDate: string;
    endDate: string;
  }): Promise<MedicionConAgregacion[]> {
    try {
      if (!params.localizacionids || params.localizacionids.length === 0) {
        throw new Error('localizacionids es requerido');
      }


      const query1 = supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_mediciones_agregadas_por_rango', {
          p_localizacionids: params.localizacionids,
          p_start_date: params.startDate,
          p_end_date: params.endDate
        }) as any;
      
      const { data, error } = await query1.range(0, 9999);

      if (error) {
        // Si la función aún no existe, retornar array vacío
        if (error.message.includes('does not exist')) {
          console.warn(
            '[SupabaseRPCService] fn_get_mediciones_agregadas_por_rango no existe aún'
          );
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getMedicionesAgregadasPorRango:', err);
      throw err;
    }
  }

  /**
   * Obtiene mediciones detalladas de un nodo con agregación inteligente según el rango
   * - Para rangos <= 1 día: Devuelve datos detallados
   * - Para rangos > 1 día: Agrupa por buckets (30min hasta 6h)
   * @param params Parámetros de la consulta
   * @returns Array de mediciones con información completa de sensores
   */
  static async getMedicionesNodoDetallado(params: {
    nodoid: number;
    metricaid?: number;
    startDate: string;
    endDate: string;
  }): Promise<any[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid es requerido y debe ser > 0');
      }

      // Validar que el rango de fechas no exceda 90 días
      if (params.startDate && params.endDate) {
        const startDate = new Date(params.startDate);
        const endDate = new Date(params.endDate);
        const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
        
        if (daysDiff > 90) {
          throw new Error('El intervalo máximo permitido es 90 días');
        }
      }

      const query = supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_mediciones_nodo_detallado', {
          p_end_date: params.endDate ? `${params.endDate}` : null,
          p_metricaid: params.metricaid || null,
          p_nodoid: params.nodoid,
          p_start_date: params.startDate ? `${params.startDate}` : null
        }) as any; // Type assertion for query builder support
      
      const { data, error } = await query;


      if (error) {
        // Si la función aún no existe, retornar array vacío
        if (error.message.includes('does not exist')) {
          console.warn(
            '[SupabaseRPCService] fn_get_mediciones_nodo_detallado no existe aún'
          );
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getMedicionesNodoDetallado:', err);
      throw err;
    }
  }

  /**
   * Obtiene SOLO las métricas disponibles para un nodo en un rango de fechas
   * Sin cargar las mediciones, solo la metadata de métricas
   * @param params Parámetros de la consulta
   * @returns Array de métricas disponibles con id y nombre
   */
  static async getMetricasDisponiblesPorNodo(params: {
    nodoid: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{ id: number; nombre: string }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid es requerido y debe ser > 0');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_metricas_disponibles_nodo', {
          p_nodoid: params.nodoid,
          p_start_date: params.startDate ? `${params.startDate} 00:00:00` : null,
          p_end_date: params.endDate ? `${params.endDate} 23:59:59` : null
        });

      if (error) {
        // Si la función aún no existe, retornar array vacío
        if (error.message.includes('does not exist')) {
          console.warn(
            '[SupabaseRPCService] fn_get_metricas_disponibles_nodo no existe aún'
          );
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((item: any) => ({
        id: item.id,
        nombre: item.nombre
      }));
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getMetricasDisponiblesPorNodo:', err);
      throw err;
    }
  }

  /**
   * Obtiene estaciones meteorológicas dinámicamente
   * Reemplaza el array hardcodeado WEATHER_STATIONS
   * @returns Array de estaciones con nodoid, nombre, referencia, coordenadas
   */
  static async getWeatherStations(): Promise<any[]> {
    try {

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_weather_stations', {});

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_weather_stations no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      // Filtrar duplicados por nodoid
      const uniqueData = data.filter((item: any, index: number, self: any[]) =>
        index === self.findIndex((t: any) => t.nodoid === item.nodoid)
      );

      return uniqueData.map((item: any) => ({
        id: item.referencia ?? String(item.nodoid),
        name: item.nodo,
        nodoid: item.nodoid,
        hasHistoric: item.has_historic,
        latitude: Number(item.latitud) || 0,
        longitude: Number(item.longitud) || 0,
      }));
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getWeatherStations:', err);
      throw err;
    }
  }

  /**
   * Obtiene resumen semanal (tabla días × métricas) para un nodo
   * @param params Parámetros: nodoid, lunes, domingo
   * @returns Array con 7 filas (una por día) con todas las métricas
   */
  static async getResumenSemanalNodo(params: {
    nodoid: number;
    lunes: string;
    domingo: string;
  }): Promise<any[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_resumen_semanal_nodo', {
          p_nodoid: params.nodoid,
          p_lunes: params.lunes,
          p_domingo: params.domingo,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_resumen_semanal_nodo no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getResumenSemanalNodo:', err);
      throw err;
    }
  }

  /**
   * Obtiene agregación semanal de una métrica para un nodo en rango de fechas
   * @param params Parámetros: nodoid, metricaid, fechaDesde, fechaHasta
   * @returns Array con filas de (iso_anual, semana_iso, valor_min, valor_max, valor_avg, cantidad_mediciones)
   */
  static async getResumenSemanalMetricaNodo(params: {
    nodoid: number;
    metricaid: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<any[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }
      if (!params.metricaid || params.metricaid <= 0) {
        throw new Error('metricaid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_resumen_semanal_metrica_nodo', {
          p_nodoid: params.nodoid,
          p_metricaid: params.metricaid,
          p_fecha_desde: params.fechaDesde,
          p_fecha_hasta: params.fechaHasta,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_resumen_semanal_metrica_nodo no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getResumenSemanalMetricaNodo:', err);
      throw err;
    }
  }

  /**
   * Obtiene métricas de un nodo consultando solo localizacion + metrica (sin medicion)
   * Versión ligera para evitar timeout, a diferencia de getMetricasDisponiblesPorNodo
   * @param params Parámetros: nodoid
   * @returns Array de { metricaid, nombre }
   */
  static async getMetricasNodo(params: {
    nodoid: number;
  }): Promise<{ metricaid: number; nombre: string }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_metricas_nodo', {
          p_nodoid: params.nodoid,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_metricas_nodo no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((item: any) => ({
        metricaid: item.metricaid,
        nombre: item.nombre,
      }));
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getMetricasNodo:', err);
      throw err;
    }
  }

  /**
   * Calcula GDD (Growing Degree Days) diarios para un nodo en un rango de fechas
   * Usa temp_out_hi (20) y temp_out_lo (21), filtra días con ambos datos
   * @param params Parámetros: nodoid, fechaDesde, fechaHasta, tempBase
   * @returns Array de { fecha, gdd_diario, tmax, tmin }
   */
  static async getGdd(params: {
    nodoid: number;
    fechaDesde: string;
    fechaHasta: string;
    tempBase: number;
  }): Promise<{ fecha: string; gdd_diario: number; tmax: number; tmin: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_gdd', {
          p_nodoid: params.nodoid,
          p_fecha_desde: params.fechaDesde,
          p_fecha_hasta: params.fechaHasta,
          p_temp_base: params.tempBase,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_gdd no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getGdd:', err);
      throw err;
    }
  }

  /**
   * Obtiene GDD semanal agregado para varios años (orquestador)
   * Internamente llama a fn_get_gdd y agrupa por ISO semana
   * @param params Parámetros: nodoid, tempBase, semanaInicio, semanaFin, anuales
   * @returns Array de { iso_anual, semana_iso, gdd_total, dias_con_datos }
   */
  static async getGddSemanalesPorAnual(params: {
    nodoid: number;
    tempBase: number;
    semanaInicio?: number;
    semanaFin?: number;
    anuales: number[];
  }): Promise<{ iso_anual: number; semana_iso: number; gdd_total: number; dias_con_datos: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }
      if (!params.anuales || params.anuales.length === 0) {
        return [];
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_gdd_semanales_por_anual', {
          p_nodoid: params.nodoid,
          p_temp_base: params.tempBase,
          p_semana_inicio: params.semanaInicio ?? 1,
          p_semana_fin: params.semanaFin ?? 53,
          p_anuales: params.anuales,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_gdd_semanales_por_anual no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getGddSemanalesPorAnual:', err);
      throw err;
    }
  }

  /**
   * Obtiene fluctuación térmica diaria para un nodo
   * @param params Parámetros: nodoid, fechaDesde, fechaHasta
   * @returns Array de { fecha, fluctuacion, tmax, tmin }
   */
  static async getFluctuacion(params: {
    nodoid: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<{ fecha: string; fluctuacion: number; tmax: number; tmin: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_fluctuacion', {
          p_nodoid: params.nodoid,
          p_fecha_desde: params.fechaDesde,
          p_fecha_hasta: params.fechaHasta,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_fluctuacion no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getFluctuacion:', err);
      throw err;
    }
  }

  /**
   * Obtiene fluctuación térmica semanal agregada por año
   * @param params Parámetros: nodoid, semanaInicio, semanaFin, anuales
   * @returns Array de { iso_anual, semana_iso, fluctuacion_avg, fluctuacion_max, dias_con_datos }
   */
  static async getFluctuacionSemanalesPorAnual(params: {
    nodoid: number;
    semanaInicio?: number;
    semanaFin?: number;
    anuales: number[];
  }): Promise<{ iso_anual: number; semana_iso: number; fluctuacion_avg: number; fluctuacion_max: number; dias_con_datos: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }
      if (!params.anuales || params.anuales.length === 0) {
        return [];
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_fluctuacion_semanales_por_anual', {
          p_nodoid: params.nodoid,
          p_semana_inicio: params.semanaInicio ?? 1,
          p_semana_fin: params.semanaFin ?? 53,
          p_anuales: params.anuales,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_fluctuacion_semanales_por_anual no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getFluctuacionSemanalesPorAnual:', err);
      throw err;
    }
  }

  /**
   * Obtiene Déficit de Presión de Vapor (DPV) diario para un nodo
   * @param params Parámetros: nodoid, fechaDesde, fechaHasta
   * @returns Array de { fecha, dpv, temp_avg, hr_avg }
   */
  static async getDpv(params: {
    nodoid: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<{ fecha: string; dpv: number; temp_avg: number; hr_avg: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_dpv', {
          p_nodoid: params.nodoid,
          p_fecha_desde: params.fechaDesde,
          p_fecha_hasta: params.fechaHasta,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_dpv no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getDpv:', err);
      throw err;
    }
  }

  /**
   * Obtiene DPV semanal agregado por año
   * @param params Parámetros: nodoid, semanaInicio, semanaFin, anuales
   * @returns Array de { iso_anual, semana_iso, dpv_avg, dpv_max, dias_con_datos }
   */
  static async getDpvSemanalesPorAnual(params: {
    nodoid: number;
    semanaInicio?: number;
    semanaFin?: number;
    anuales: number[];
  }): Promise<{ iso_anual: number; semana_iso: number; dpv_avg: number; dpv_max: number; dias_con_datos: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid inválido');
      }
      if (!params.anuales || params.anuales.length === 0) {
        return [];
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_dpv_semanales_por_anual', {
          p_nodoid: params.nodoid,
          p_semana_inicio: params.semanaInicio ?? 1,
          p_semana_fin: params.semanaFin ?? 53,
          p_anuales: params.anuales,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn('[SupabaseRPCService] fn_get_dpv_semanales_por_anual no existe aún');
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getDpvSemanalesPorAnual:', err);
      throw err;
    }
  }

  /**
   * Obtiene semanas con datos disponibles para un nodo y métrica
   * Retorna un mapa de año → Set<semanas> para usar en validación de UI
   * @param params Parámetros de la consulta
   * @returns Array de { year_num, week_num } agrupado por disponibilidad
   */
  static async getSemanasConDatos(params: {
    nodoid: number;
    metricaNombre: string;
    startYear?: number;
    endYear?: number;
  }): Promise<{ year_num: number; week_num: number }[]> {
    try {
      if (!params.nodoid || params.nodoid <= 0) {
        throw new Error('nodoid es requerido y debe ser > 0');
      }

      if (!params.metricaNombre) {
        throw new Error('metricaNombre es requerido');
      }

      const { data, error } = await supabaseAuth
        .schema(DB_SCHEMA)
        .rpc('fn_get_semanas_con_datos', {
          p_nodoid: params.nodoid,
          p_metrica_nombre: params.metricaNombre,
          p_start_year: params.startYear ?? null,
          p_end_year: params.endYear ?? null,
        });

      if (error) {
        if (error.message.includes('does not exist')) {
          console.warn(
            '[SupabaseRPCService] fn_get_semanas_con_datos no existe aún'
          );
          return [];
        }
        throw new Error(`RPC error: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((item: any) => ({
        year_num: item.year_num,
        week_num: item.week_num,
      }));
    } catch (err: any) {
      console.error('[SupabaseRPCService] Error en getSemanasConDatos:', err);
      throw err;
    }
  }
}

export default SupabaseRPCService;
